import type { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { HighlightText, Tag } from '../../components';
import { Destinations } from './destinations';
import { eventTypeInfo, formatTime, STATUS, stopNav, toEventView } from './event-model';
import type { ScheduledEvent, UntitledEvent } from './graphql';

interface Props {
  events?: ScheduledEvent[];
  untitledEvents?: UntitledEvent[];
  favoriteEvents: Set<string>;
  search?: string;
}

/*
 * TODO: real error count once errors are surfaced. Mock hidden for now.
 * const ERROR_MOCK = 3;
 */

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 13px;
  color: var(--white);

  thead th {
    padding: 12px 7px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--white35);
    white-space: nowrap;
  }

  tbody td {
    padding: 13px 7px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: middle;
    font-size: 13px;
  }

  th:first-child,
  td:first-child {
    padding-left: 18px;
  }

  th:last-child,
  td:last-child {
    padding-right: 18px;
  }

  /* Destinations is the last column — align its header to the right like its cells. */
  th:last-child {
    text-align: right;
  }

  .flags {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  /* Status tag label always uppercase, regardless of state. */
  .status {
    text-transform: uppercase;
  }

  .event .title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .event .title {
    font-weight: 700;
    color: var(--white);
  }

  .event .star {
    color: #ffd27a;
    font-size: 12px;
  }

  .event .ids {
    display: flex;
    gap: 10px;
    margin-top: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--white60);
  }

  .time {
    color: var(--white60);
    white-space: nowrap;
  }

  .dest {
    display: flex;
    justify-content: flex-end;
  }
`;

const Row = styled.tr<{ $highPriority: boolean }>`
  cursor: pointer;
  transition: background var(--transition);
  ${({ $highPriority }): string =>
    $highPriority
      ? 'box-shadow: rgb(255, 80, 95) 3px 0px 0px 0px inset; background: rgba(255, 80, 95, 0.06);'
      : ''}

  &:hover {
    background: ${({ $highPriority }): string =>
      $highPriority ? 'rgba(255, 80, 95, 0.1)' : 'rgba(255, 255, 255, 0.03)'};
  }
`;

export const EventsTable = ({
  events,
  untitledEvents,
  favoriteEvents,
  search
}: Props): ReactElement => {
  const history = useHistory();

  const all = [...(events ?? []), ...(untitledEvents ?? [])];

  return (
    <Table>
      <thead>
        <tr>
          <th>Status</th>
          <th>Event</th>
          <th>Time</th>
          <th>Setup</th>
          <th>Flags</th>
          <th>Alerts</th>
          <th>Destinations</th>
        </tr>
      </thead>
      <tbody>
        {all.map((event) => {
          const view = toEventView(event);
          const badge = STATUS[view.status];
          const type = eventTypeInfo(view.scheduled?.eventType);
          const favorite = favoriteEvents.has(view.eid);

          return (
            <Row
              key={view.to}
              $highPriority={view.highPriority}
              onClick={(): void => {
                history.push(view.to);
              }}
            >
              <td className='status'>
                {view.status === 'live' ? (
                  <Tag
                    size='small'
                    className='cc-green'
                    style={{ border: 'none' }}
                    icon='fas fa-circle cc-blink-slow'
                    iconSize={7}
                  >
                    {badge.label}
                  </Tag>
                ) : (
                  <Tag size='small' className={badge.className} icon={badge.icon}>
                    {badge.label}
                  </Tag>
                )}
              </td>

              <td className='event'>
                <div className='title-row'>
                  {favorite && <i className='star fas fa-star' />}
                  <span className='title'>
                    <HighlightText highlight={search}>{view.title}</HighlightText>
                  </span>
                </div>
                <div className='ids'>
                  {view.cid && (
                    <span>
                      CID: <HighlightText highlight={search}>{view.cid}</HighlightText>
                    </span>
                  )}
                  <span>
                    EID: <HighlightText highlight={search}>{view.eid}</HighlightText>
                  </span>
                </div>
              </td>

              <td className='time'>{formatTime(view.start, view.end)}</td>

              <td>
                {view.scheduled && (
                  <Tag size='small'>
                    {type.label}
                  </Tag>
                )}
              </td>

              <td>
                <div className='flags'>
                  {view.isFirstEvent && (
                    <Tag size='small' className='cc-amber'>
                      First Event
                    </Tag>
                  )}
                  {view.receiveIsoRecordings && (
                    <Tag size='small' className='cc-neutral'>
                      ISO
                    </Tag>
                  )}
                  {view.hasAudioMix && (
                    <Tag size='small' className='cc-neutral'>
                      Audio Mix
                    </Tag>
                  )}
                  {view.hasVideographer && (
                    <Tag size='small' className='cc-neutral'>
                      DVG
                    </Tag>
                  )}
                </div>
              </td>

              <td>
                {/* Mock error/alerts tag — hidden for now.
                <Tag size='small' className='cc-red' icon='fas fa-exclamation-triangle'>
                  {ERROR_MOCK}
                </Tag> */}
              </td>

              <td>
                {view.scheduled && (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div className='dest' onClick={stopNav}>
                    <Destinations {...view.scheduled} />
                  </div>
                )}
              </td>
            </Row>
          );
        })}
      </tbody>
    </Table>
  );
};
